import axios from 'axios'
import {
  listInstructionContent,
  createInstructionContent,
  updateInstructionContent,
  addInstructionContentVersion,
  deleteInstructionContent,
  getUploadUrl,
  instructionContentUrl,
  InstructionContent,
  InstructionContentPayload,
} from '@training24/course/src/instruction'

interface InstructionContentState {
  library: InstructionContent[]
  loaded: boolean
}

export type { InstructionContent }

export default {
  namespaced: true,

  state: (): InstructionContentState => ({
    library: [],
    loaded: false,
  }),

  getters: {
    isLoaded: (state: InstructionContentState): boolean => state.loaded,
    getLibrary: (state: InstructionContentState): InstructionContent[] => state.library,
    // Nur Bausteine mit mindestens einer hochgeladenen Datei sind in Modulen verwendbar.
    getUsable: (state: InstructionContentState): InstructionContent[] =>
      state.library.filter((c) => c.currentVersion > 0),
  },

  actions: {
    async LOAD(context: any, opts?: { force?: boolean }) {
      if (context.state.loaded && !(opts && opts.force)) return
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const items = await listInstructionContent(orgId, jwt)
      context.commit('SET_LIBRARY', items)
    },

    async GET_UPLOAD_URL(
      context: any,
      { fileName, contentType }: { fileName: string; contentType: string }
    ) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      return getUploadUrl(orgId, fileName, contentType, jwt)
    },

    // Pre-signed Anzeige-URL zur aktuellen Version eines Dokuments (Vorschau).
    async GET_DOC_URL(context: any, { contentId, contentOrg }: { contentId: string; contentOrg?: string }) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      return instructionContentUrl(orgId, contentId, contentOrg || orgId, jwt)
    },

    async CREATE_CONTENT(context: any, payload: InstructionContentPayload) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await createInstructionContent(orgId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async ADD_VERSION(
      context: any,
      { contentId, payload }: { contentId: string; payload: any }
    ) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await addInstructionContentVersion(orgId, contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async UPDATE_CONTENT(
      context: any,
      { contentId, payload }: { contentId: string; payload: Partial<InstructionContentPayload> }
    ) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      const item = await updateInstructionContent(orgId, contentId, payload, jwt)
      context.commit('UPSERT', item)
      return item
    },

    async DELETE_CONTENT(context: any, contentId: string) {
      const orgId = context.rootGetters['organisation/getOrganisationId']
      const jwt = context.rootGetters['getJWT']
      await deleteInstructionContent(orgId, contentId, jwt)
      context.commit('REMOVE', contentId)
    },

    // Komfort: ein eigenes Dokument in einem Schritt zum Baustein machen
    // (pre-signed Upload + Metadaten + erste Version).
    async UPLOAD_OWN_BAUSTEIN(
      context: any,
      { file, title, confirmationItems }: { file: File; title?: string; confirmationItems?: string[] }
    ): Promise<InstructionContent> {
      const contentType = file.type
      const fileType: 'pdf' | 'docx' = contentType === 'application/pdf' ? 'pdf' : 'docx'

      const { uploadUrl, key } = await context.dispatch('GET_UPLOAD_URL', {
        fileName: file.name,
        contentType,
      })
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } })

      const content = await context.dispatch('CREATE_CONTENT', {
        title: title || file.name,
        fileType,
        confirmationItems: confirmationItems || [],
      })
      return context.dispatch('ADD_VERSION', {
        contentId: content.contentId,
        payload: { fileName: file.name, fileKey: key, fileType },
      })
    },
  },

  mutations: {
    SET_LIBRARY(state: InstructionContentState, items: InstructionContent[]) {
      state.library = items
      state.loaded = true
    },
    UPSERT(state: InstructionContentState, item: InstructionContent) {
      const others = state.library.filter((c) => c.contentId !== item.contentId)
      state.library = [...others, item]
    },
    REMOVE(state: InstructionContentState, contentId: string) {
      state.library = state.library.filter((c) => c.contentId !== contentId)
    },
  },
}
